import { useState } from 'react';
import { HelpCircle, BookOpen, Play, MessageCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TourType } from './PlatformTour';

interface HelpMenuProps {
  onStartTour: (tourType: TourType) => void;
  onAskElla: () => void;
}

export function HelpMenu({ onStartTour, onAskElla }: HelpMenuProps) {
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  const glossaryTerms = [
    {
      term: 'Cap Table',
      definition:
        'A capitalization table shows who owns what in your company - shareholders, their share counts, and ownership percentages.',
    },
    {
      term: 'Dilution',
      definition:
        "When new shares are issued, existing shareholders' ownership percentage decreases. This is called dilution.",
    },
    {
      term: 'Vesting',
      definition:
        'A schedule where shares or options are earned over time, typically used for employee compensation.',
    },
    {
      term: 'Pre-money Valuation',
      definition: "Your company's value before new investment comes in.",
    },
    {
      term: 'Post-money Valuation',
      definition: "Your company's value after new investment. Pre-money + investment amount.",
    },
    {
      term: 'Authorized Shares',
      definition:
        'The maximum number of shares your company is allowed to issue according to your articles of incorporation.',
    },
    {
      term: 'Issued Shares',
      definition: 'Shares that have actually been distributed to shareholders.',
    },
    {
      term: 'RLS',
      definition:
        "Row Level Security - ensures users can only see data they're authorized to access.",
    },
    {
      term: 'CIPC',
      definition:
        "Companies and Intellectual Property Commission - South Africa's company registry.",
    },
  ];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-tour="ella-button">
            <HelpCircle className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Help & Guidance</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={onAskElla}>
            <MessageCircle className="mr-2 h-4 w-4" />
            Ask Ella AI
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Guided Tours
          </DropdownMenuLabel>

          <DropdownMenuItem onClick={() => onStartTour('dashboard')}>
            <Play className="mr-2 h-4 w-4" />
            Platform Overview
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => onStartTour('captable')}>
            <Play className="mr-2 h-4 w-4" />
            Cap Table Tour
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => onStartTour('compliance')}>
            <Play className="mr-2 h-4 w-4" />
            Compliance Tour
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => onStartTour('dataroom')}>
            <Play className="mr-2 h-4 w-4" />
            Data Room Tour
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => setGlossaryOpen(true)}>
            <BookOpen className="mr-2 h-4 w-4" />
            View Glossary
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={glossaryOpen} onOpenChange={setGlossaryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Platform Glossary</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {glossaryTerms.map(({ term, definition }) => (
              <div key={term} className="border-l-2 border-primary pl-4">
                <h4 className="font-semibold text-foreground">{term}</h4>
                <p className="text-sm text-muted-foreground mt-1">{definition}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
